import { useMemo } from 'react';
import {
  hasAcademyManagementRole,
  hasClubManagementRole,
  hasEntitlement,
  hasRole,
  buildOrgEntitlements,
  TierFeatureKey,
  type OrgEntitlements,
} from '@court-sync/shared';
import { useAuthStore } from '@/store/auth';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';
import { useManagementDashboardFeatureAccess } from '@/hooks/useFeatureAccess';
import type { ScheduleOccurrenceSourceType } from '@/utils/scheduleOccurrences';

export interface ScheduleSourceTypeAccess {
  canShowLessons: boolean;
  canShowCourtHire: boolean;
  canShowClosures: boolean;
  canShowPlayEvents: boolean;
  /** Source types the user may toggle in the calendar filter bar. */
  availableSourceTypes: ScheduleOccurrenceSourceType[];
  canOperateClub: boolean;
  hasAcademyManagement: boolean;
  isClubOnlyOperator: boolean;
  hasFacilityLayer: boolean;
}

export function useScheduleSourceTypeAccess(): ScheduleSourceTypeAccess {
  const { user } = useAuthStore();
  const { entitlements, hasClubManagement, isLoading: clubEntitlementsLoading } =
    useClubModuleAccess();
  const { currentTier, tierResolved } = useManagementDashboardFeatureAccess(
    TierFeatureKey.MATCH_PLAY
  );

  return useMemo(() => {
    const hasAcademyManagement = hasAcademyManagementRole(user);
    const hasClubRole = hasClubManagementRole(user);
    const isAcademyOwner = hasRole(user, 'academy_owner');
    const canOperateClub = hasClubRole || isAcademyOwner;
    const hasClubModule = canOperateClub && hasClubManagement && !clubEntitlementsLoading;

    const orgEntitlements: OrgEntitlements =
      entitlements ?? buildOrgEntitlements({ clubManagement: false, tier: 'starter' });

    const canShowLessons =
      hasAcademyManagement || (canOperateClub && hasClubModule);

    const canShowCourtHire =
      hasClubModule &&
      hasEntitlement(orgEntitlements, TierFeatureKey.COURT_HIRE);

    const canShowClosures = hasClubModule;

    const canShowPlayEvents =
      tierResolved &&
      hasEntitlement(orgEntitlements, TierFeatureKey.MATCH_PLAY, currentTier);

    const availableSourceTypes: ScheduleOccurrenceSourceType[] = [];
    if (canShowLessons) availableSourceTypes.push('lesson');
    if (canShowCourtHire) availableSourceTypes.push('court_hire');
    if (canShowClosures) availableSourceTypes.push('closure');
    if (canShowPlayEvents) availableSourceTypes.push('play_event');

    const isClubOnlyOperator = !hasAcademyManagement && canOperateClub && hasClubModule;
    const hasFacilityLayer =
      canShowCourtHire || canShowClosures || isClubOnlyOperator || canShowPlayEvents;

    return {
      canShowLessons,
      canShowCourtHire,
      canShowClosures,
      canShowPlayEvents,
      availableSourceTypes,
      canOperateClub,
      hasAcademyManagement,
      isClubOnlyOperator,
      hasFacilityLayer,
    };
  }, [
    user,
    entitlements,
    hasClubManagement,
    clubEntitlementsLoading,
    currentTier,
    tierResolved,
  ]);
}
